import React, { useCallback, useMemo, useRef } from 'react';
import {
  useFullState,
  useWriteContext,
  useDimensions,
} from '../utils/use-context';
import { useCanvasTransformationScale } from '../utils/canvas-transformation-context';
import { isLeftClick } from '../utils/is-left-click';

const CORNER_HANDLE_SIZE = 10;
const EDGE_HANDLE_LENGTH = 64;
const EDGE_HANDLE_THICKNESS = 12;
const EDGE_BORDER_WIDTH = 2;
const EDGE_BORDER_RADIUS = 99;

type HandleType =
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'top-left'
  | 'top-right'
  | 'bottom-right'
  | 'bottom-left';

const MaskHandle: React.FC<{
  type: HandleType;
  maskX: number;
  maskY: number;
  maskWidth: number;
  maskHeight: number;
}> = ({ type, maskX, maskY, maskWidth, maskHeight }) => {
  const scale = useCanvasTransformationScale();
  const cornerSize = Math.round(CORNER_HANDLE_SIZE / scale);
  const edgeLength = Math.round(EDGE_HANDLE_LENGTH / scale);
  const edgeThickness = Math.round(EDGE_HANDLE_THICKNESS / scale);
  const edgeBorderWidth = EDGE_BORDER_WIDTH / scale;
  const edgeBorderRadius = EDGE_BORDER_RADIUS / scale;
  const { setState } = useWriteContext();
  const { compositionWidth, compositionHeight } = useDimensions();

  const cornerMargin = -cornerSize / 2 - 1 / scale;

  const style: React.CSSProperties = useMemo(() => {
    const brandAccent =
      'var(--color-editor-starter-accent, #3b82f6)' as React.CSSProperties['borderColor'];
    const baseStyle: React.CSSProperties = {
      borderColor: brandAccent,
      borderStyle: 'solid',
      borderWidth: 1 / scale,
      backgroundColor: 'white',
    };

    if (type === 'top') {
      return {
        ...baseStyle,
        borderColor: '#ffffff',
        backgroundColor: brandAccent,
        borderWidth: edgeBorderWidth,
        borderRadius: edgeBorderRadius,
        left: maskX + maskWidth / 2 - edgeLength / 2,
        top: maskY - edgeThickness / 2,
        width: edgeLength,
        height: edgeThickness,
        cursor: 'ns-resize',
      };
    }
    if (type === 'top-left') {
      return {
        ...baseStyle,
        left: maskX + cornerMargin,
        top: maskY + cornerMargin,
        width: cornerSize,
        height: cornerSize,
        cursor: 'nwse-resize',
      };
    }
    if (type === 'top-right') {
      return {
        ...baseStyle,
        left: maskX + maskWidth + cornerMargin,
        top: maskY + cornerMargin,
        width: cornerSize,
        height: cornerSize,
        cursor: 'nesw-resize',
      };
    }
    if (type === 'bottom') {
      return {
        ...baseStyle,
        borderColor: '#ffffff',
        backgroundColor: brandAccent,
        borderWidth: edgeBorderWidth,
        borderRadius: edgeBorderRadius,
        left: maskX + maskWidth / 2 - edgeLength / 2,
        top: maskY + maskHeight - edgeThickness / 2,
        width: edgeLength,
        height: edgeThickness,
        cursor: 'ns-resize',
      };
    }
    if (type === 'bottom-right') {
      return {
        ...baseStyle,
        left: maskX + maskWidth + cornerMargin,
        top: maskY + maskHeight + cornerMargin,
        width: cornerSize,
        height: cornerSize,
        cursor: 'nwse-resize',
      };
    }
    if (type === 'bottom-left') {
      return {
        ...baseStyle,
        left: maskX + cornerMargin,
        top: maskY + maskHeight + cornerMargin,
        width: cornerSize,
        height: cornerSize,
        cursor: 'nesw-resize',
      };
    }
    if (type === 'left') {
      return {
        ...baseStyle,
        borderColor: '#ffffff',
        backgroundColor: brandAccent,
        borderWidth: edgeBorderWidth,
        borderRadius: edgeBorderRadius,
        left: maskX - edgeThickness / 2,
        top: maskY + maskHeight / 2 - edgeLength / 2,
        width: edgeThickness,
        height: edgeLength,
        cursor: 'ew-resize',
      };
    }
    if (type === 'right') {
      return {
        ...baseStyle,
        borderColor: '#ffffff',
        backgroundColor: brandAccent,
        borderWidth: edgeBorderWidth,
        borderRadius: edgeBorderRadius,
        left: maskX + maskWidth - edgeThickness / 2,
        top: maskY + maskHeight / 2 - edgeLength / 2,
        width: edgeThickness,
        height: edgeLength,
        cursor: 'ew-resize',
      };
    }
    return baseStyle;
  }, [
    type,
    maskX,
    maskY,
    maskWidth,
    maskHeight,
    edgeLength,
    edgeThickness,
    edgeBorderWidth,
    edgeBorderRadius,
    cornerSize,
    cornerMargin,
    scale,
  ]);

  const isDraggingRef = useRef(false);
  const handlersRef = useRef<{
    onPointerMove: ((e: PointerEvent) => void) | null;
    onPointerUp: (() => void) | null;
  }>({ onPointerMove: null, onPointerUp: null });

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation();
      if (!isLeftClick(e)) {
        return;
      }

      // 기존 이벤트 리스너 정리
      if (handlersRef.current.onPointerMove) {
        window.removeEventListener(
          'pointermove',
          handlersRef.current.onPointerMove,
        );
      }
      if (handlersRef.current.onPointerUp) {
        window.removeEventListener(
          'pointerup',
          handlersRef.current.onPointerUp,
        );
      }

      const initialX = e.clientX;
      const initialY = e.clientY;
      const initialMaskX = maskX;
      const initialMaskY = maskY;
      const initialMaskWidth = maskWidth;
      const initialMaskHeight = maskHeight;

      isDraggingRef.current = true;

      const onPointerMove = (pointerMoveEvent: PointerEvent) => {
        if (!isDraggingRef.current) return;

        const offsetX = (pointerMoveEvent.clientX - initialX) / scale;
        const offsetY = (pointerMoveEvent.clientY - initialY) / scale;

        let newX = initialMaskX;
        let newY = initialMaskY;
        let newWidth = initialMaskWidth;
        let newHeight = initialMaskHeight;

        const isTopHandle =
          type === 'top' || type === 'top-left' || type === 'top-right';
        const isBottomHandle =
          type === 'bottom' ||
          type === 'bottom-left' ||
          type === 'bottom-right';
        const isLeftHandle =
          type === 'left' || type === 'top-left' || type === 'bottom-left';
        const isRightHandle =
          type === 'right' || type === 'top-right' || type === 'bottom-right';

        if (isTopHandle) {
          // 상하 핸들: 양쪽이 같이 조절됨
          const centerY = initialMaskY + initialMaskHeight / 2;
          const newHalfHeight = Math.max(0.5, initialMaskHeight / 2 - offsetY);
          newY = Math.max(0, centerY - newHalfHeight);
          newHeight = Math.max(1, newHalfHeight * 2);
          // 위쪽 경계 체크
          if (newY < 0) {
            newY = 0;
            newHeight = initialMaskHeight + initialMaskY;
          }
          // 아래쪽 경계 체크
          if (newY + newHeight > compositionHeight) {
            newHeight = compositionHeight - newY;
          }
        } else if (isBottomHandle) {
          // 상하 핸들: 양쪽이 같이 조절됨
          const centerY = initialMaskY + initialMaskHeight / 2;
          const newHalfHeight = Math.max(0.5, initialMaskHeight / 2 + offsetY);
          newY = Math.max(0, centerY - newHalfHeight);
          newHeight = Math.max(1, newHalfHeight * 2);
          // 위쪽 경계 체크
          if (newY < 0) {
            newY = 0;
            newHeight = initialMaskHeight + initialMaskY;
          }
          // 아래쪽 경계 체크
          if (newY + newHeight > compositionHeight) {
            newHeight = compositionHeight - newY;
          }
        }

        if (isLeftHandle) {
          // 좌우 핸들: 양쪽이 같이 조절됨
          const centerX = initialMaskX + initialMaskWidth / 2;
          const newHalfWidth = Math.max(0.5, initialMaskWidth / 2 - offsetX);
          newX = Math.max(0, centerX - newHalfWidth);
          newWidth = Math.max(1, newHalfWidth * 2);
          // 왼쪽 경계 체크
          if (newX < 0) {
            newX = 0;
            newWidth = initialMaskWidth + initialMaskX;
          }
          // 오른쪽 경계 체크
          if (newX + newWidth > compositionWidth) {
            newWidth = compositionWidth - newX;
          }
        } else if (isRightHandle) {
          // right - 좌우 핸들: 양쪽이 같이 조절됨
          const centerX = initialMaskX + initialMaskWidth / 2;
          const newHalfWidth = Math.max(0.5, initialMaskWidth / 2 + offsetX);
          newX = Math.max(0, centerX - newHalfWidth);
          newWidth = Math.max(1, newHalfWidth * 2);
          // 왼쪽 경계 체크
          if (newX < 0) {
            newX = 0;
            newWidth = initialMaskWidth + initialMaskX;
          }
          // 오른쪽 경계 체크
          if (newX + newWidth > compositionWidth) {
            newWidth = compositionWidth - newX;
          }
        }

        setState({
          update: (state) => {
            if (!state.undoableState.mask) {
              return state;
            }
            return {
              ...state,
              undoableState: {
                ...state.undoableState,
                mask: {
                  ...state.undoableState.mask,
                  aspectRatio: 'custom',
                  x: Math.round(newX),
                  y: Math.round(newY),
                  width: Math.round(newWidth),
                  height: Math.round(newHeight),
                },
              },
            };
          },
          commitToUndoStack: false,
        });
      };

      const onPointerUp = () => {
        isDraggingRef.current = false;
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        handlersRef.current.onPointerMove = null;
        handlersRef.current.onPointerUp = null;
        setState({
          update: (state) => state,
          commitToUndoStack: true,
        });
      };

      handlersRef.current.onPointerMove = onPointerMove;
      handlersRef.current.onPointerUp = onPointerUp;

      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('pointerup', onPointerUp, { once: true });
    },
    [
      maskX,
      maskY,
      maskWidth,
      maskHeight,
      scale,
      setState,
      type,
      compositionWidth,
      compositionHeight,
    ],
  );

  return (
    <div
      className="pointer-events-auto absolute z-[1000]"
      onPointerDown={onPointerDown}
      style={style}
      onPointerMove={(e) => e.stopPropagation()}
    />
  );
};

export const VideoMaskHandles: React.FC = () => {
  const { undoableState } = useFullState();
  const mask = undoableState.mask;

  if (!mask || !mask.enabled) {
    return null;
  }

  const handleTypes: HandleType[] = [
    'top',
    'right',
    'bottom',
    'left',
    'top-left',
    'top-right',
    'bottom-right',
    'bottom-left',
  ];

  return (
    <>
      {handleTypes.map((type) => (
        <MaskHandle
          key={type}
          type={type}
          maskX={mask.x}
          maskY={mask.y}
          maskWidth={mask.width}
          maskHeight={mask.height}
        />
      ))}
    </>
  );
};
